import * as React from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import type Slider from 'react-slick';

import { Baggage } from '../../../../BaggageService/types';
import { Passenger } from '../../../types';
import { useTheme } from '../../../../theme';
import { useIsMobile } from '../../../../utils';
import { getBaggageTab, getCurrentSlide } from '../../store/selector';
import SlickSlider from '../../../../SlickSlider';
import { MAX_VISIBLE_BAGGAGE_COLUMNS, baggageColumnsSliderSettings } from '../../const';
import PassengerBaggageControl from './PassengerBaggageControl/PassengerBaggageControl';

export interface Props {
	align?: 'center' | 'right';
	name: string;
	number: number;
	includedSegment?: boolean;
	baggageByRfics: Baggage[][];
	segmentId?: string;
	mobileSelectedServiceId?: string;
	isAvailable?: boolean;
	passenger: Passenger;
}

const BaggagePassenger: React.FC<Props> = props => {
	const theme = useTheme('UpgradeBaggageDialog').BaggagePassenger;

	const baggageTab = useSelector(getBaggageTab);

	const isMobile = useIsMobile();
	const isUnavailable = props.isAvailable === false;

	const isSliderMode = !isMobile && !baggageTab && props.baggageByRfics.length > MAX_VISIBLE_BAGGAGE_COLUMNS;

	const currentSlide = useSelector(getCurrentSlide);
	const sliderRef = React.useRef<Slider>(null);

	React.useEffect(() => {
		if (isSliderMode) {
			sliderRef.current?.slickGoTo(currentSlide);
		}
	}, [currentSlide, isSliderMode]);

	const services: Baggage[][] = React.useMemo(() => {
		if (isMobile && !baggageTab) {
			return [props.baggageByRfics.find(service => service[0].rfics === props.mobileSelectedServiceId)];
		}

		return props.baggageByRfics;
	}, [props.baggageByRfics, props.mobileSelectedServiceId, isMobile, baggageTab]);

	const controls = services.map((baggageServices, index) => (
		<PassengerBaggageControl
			key={index}
			baggageServices={baggageServices}
			passenger={props.passenger}
			segmentId={props.segmentId}
			isUnavailable={isUnavailable}
		/>
	));

	return (
		<div
			className={cn(theme.root, { [theme.disabled]: isUnavailable })}
			data-test-id={`segment-${props?.segmentId ?? 'all'}-passenger-${props.passenger?.id}`}
		>
			<div className={cn(theme.row)}>
				<div className={theme.name_wrapper}>
					<div className={theme.number}>{props.number}</div>
					<div className={theme.name}>{props.name}</div>
				</div>

				{isSliderMode ? (
					<SlickSlider
						ref={sliderRef}
						className={theme.slider}
						{...baggageColumnsSliderSettings}
						arrows={false}
					>
						{controls}
					</SlickSlider>
				) : (
					<div className={cn(theme.baggages, { [theme.baggages_rightAlign]: props.align === 'right' })}>
						{controls}
					</div>
				)}
			</div>
		</div>
	);
};

export default BaggagePassenger;
